import { Link } from "react-router-dom";
import { ArrowLeft, Heart, Clock, Sparkles, ArrowUpRight, MessageCircle, CalendarCheck, Info, Check, Droplets } from "lucide-react";
import InternalNav from "@/components/InternalNav";
import InlineText from "@/components/InlineText";
import InlineList from "@/components/InlineList";
import BlockControls from "@/components/BlockControls";
import { MEDIA } from "@/content/media";
import { useContent } from "@/lib/ContentProvider";

const IMG = {
  bain: MEDIA.experiences.bainDeFleurs,
  petitDej: MEDIA.experiences.petitDejeuner1,
  decorNuitRouge: MEDIA.experiences.decorNuitRouge,
};

const WA_TEMPLATE = (title) => `Bonjour, j'ai déjà réservé mon séjour et je souhaite ajouter ${title}.`;

const EXPS = [
  {
    id: "bain-de-fleurs",
    title: "Bain de fleurs",
    eyebrow: "Rituel sensoriel",
    sub: "Un bain fleuri préparé pour vous, dans le calme du jardin.",
    intro: "Un moment simple, calme et intime, à vivre à votre rythme.",
    image: IMG.bain,
    price: "70€",
    unit: "par bain",
    Icon: Heart,
    essential: [
      "Préparé pour 16h le jour de votre arrivée",
      "Fleurs fraîches et séchées",
      "Bassin extérieur privé",
      "Accès libre",
      "Conçu pour deux",
    ],
    practical: [
      "Jusqu'à 400 L d'eau chaude disponibles, selon l'ensoleillement",
      "Durée conseillée : 30 à 45 minutes",
      "Le bain reste accessible au maximum 12 heures après votre arrivée",
      "Option à réserver en supplément",
      "Réservation à effectuer au minimum 96h (4 jours) avant votre arrivée",
    ],
    vidangeCta: {
      label: "Voir comment vider le bain de fleurs",
      href: "/guide/bassin?mode=fleurs#fonctionnement-bassin",
    },
    precautions: {
      title: "🌿 Quelques précautions avant de profiter du bain",
      intro:
        "Le bain floral est préparé avec des fleurs fraîches soigneusement sélectionnées et adaptées à cette expérience.",
      avoidLabel: "À lire avant de sauter dans le bain",
      avoid: [
        "allergie connue aux fleurs, plantes ou parfums naturels",
        "peau très sensible, irritée ou présentant une lésion",
        "problème dermatologique en cours",
      ],
      tipsLabel: "Nous vous conseillons également",
      tips: [
        "une durée de bain de 30 à 45 minutes",
        "un rinçage à l'eau claire après utilisation",
      ],
      footer:
        "En cas de grossesse, d'allaitement ou de doute médical particulier, il est préférable de demander conseil à un professionnel de santé avant utilisation.",
    },
    windowSymbol: (
      <svg viewBox="0 0 60 80" fill="none" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M30 24 C26 28, 26 32, 30 36 C34 32, 34 28, 30 24 Z" />
        <path d="M22 44 C18 48, 18 52, 22 56 C26 52, 26 48, 22 44 Z" opacity="0.75" />
        <path d="M38 44 C34 48, 34 52, 38 56 C42 52, 42 48, 38 44 Z" opacity="0.75" />
      </svg>
    ),
  },
  {
    id: "petit-dejeuner",
    title: "Petit déjeuner",
    eyebrow: "Au refuge",
    sub: "Servi au refuge, dans un cadre calme et intime.",
    intro: "Produits simples et frais, préparations maison.",
    image: IMG.petitDej,
    price: "25€",
    unit: "par personne / matin",
    Icon: Clock,
    menu: [
      ["Œufs brouillés", "Au choix : bacon ou fromage (à préciser à l'avance)"],
      ["Pain toasté", "Beurre maison · nature ou thym"],
      ["Confitures maison", "Au gré des saisons"],
      ["Fruits frais", "De saison"],
      ["Jus d'orange", "Pressé"],
      ["Café", "Ou thé sur demande"],
      ["Pancakes maison", "Chocolat"],
    ],
    essential: [
      "Servi au refuge, dans un cadre calme et intime",
      "De 9H à 10H, du lundi au samedi",
      "Pas de service le dimanche",
      "Moment calme au réveil",
      "Produits simples et frais",
      "Préparation maison",
    ],
    practical: [
      "Option à réserver en supplément",
      "À ajouter lors de votre réservation en ligne",
      "Ou faire une demande via WhatsApp",
      "Réservation à effectuer au minimum 96 heures (4 jours) avant votre arrivée",
    ],
    windowSymbol: (
      <svg viewBox="0 0 60 80" fill="none" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 34 L40 34 L38 52 C37.7 54, 36 55.5, 34 55.5 L26 55.5 C24 55.5, 22.3 54, 22 52 Z" />
        <path d="M40 38 C44 38, 45 41, 45 44 C45 47, 44 50, 40 50" />
        <path d="M25 26 C25 30, 23 31, 23 34" opacity="0.7" />
        <path d="M30 25 C30 29, 28 30, 28 33" opacity="0.7" />
        <path d="M35 26 C35 30, 33 31, 33 34" opacity="0.7" />
      </svg>
    ),
  },
  {
    id: "decors",
    title: "Décors du Refuge",
    eyebrow: "Ambiance",
    sub: "Une ambiance décorée pour célébrer votre séjour.",
    intro: "Une mise en scène élégante préparée avant votre arrivée.",
    image: IMG.decorNuitRouge,
    price: "50€",
    unit: "par séjour",
    Icon: Sparkles,
    ambiance: {
      name: "Nuit Rouge",
      status: "Disponible",
      tagline: "Ambiance romantique et passionnée",
      features: [
        "Pétales de roses",
        "Bougies",
        "Décor romantique dans la chambre",
      ],
      note: "Préparé pour votre arrivée",
    },
    essential: [
      "Décor installé pour votre arrivée",
      "Ambiance romantique dans la chambre",
      "Bougies et pétales",
      "Mise en scène élégante",
    ],
    practical: [
      "Le décor est une option à réserver en supplément",
      "Préparé avant votre arrivée",
      "Réservation à effectuer au minimum 96h (4 jours) avant votre arrivée",
      "Les bougies utilisées sont des bougies sans flamme pour des raisons de sécurité",
    ],
    windowSymbol: (
      <svg viewBox="0 0 60 80" fill="none" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M30 20 L33 32 L45 32 L35 40 L39 52 L30 44 L21 52 L25 40 L15 32 L27 32 Z" />
      </svg>
    ),
  },
];

function ExperienceWindow({ item, index, mediaUrl }) {
  const outerRadius = "9999px 9999px 2px 2px";
  const innerRadius = "9999px 9999px 1px 1px";

  return (
    <button
      type="button"
      data-testid={`exp-window-${item.id}`}
      aria-label={`Voir ${item.title}`}
      onClick={() => {
        const el = document.getElementById(item.id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }}
      className="group block w-full text-left cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--cr-ruby)]/30 rounded-md cr-reveal"
      style={{ animationDelay: `${0.08 * index + 0.05}s` }}
    >
      <div className="relative mx-auto w-full max-w-[160px] sm:max-w-[200px] md:max-w-[230px]">
        <div
          className="relative overflow-hidden transition-all duration-700 group-hover:-translate-y-1"
          style={{
            aspectRatio: "1 / 1.55",
            borderRadius: outerRadius,
            border: "1px solid rgba(139, 37, 48, 0.28)",
            background: "transparent",
          }}
        >
          <div
            className="absolute pointer-events-none overflow-hidden"
            style={{ top: 5, left: 5, right: 5, bottom: 5, borderRadius: innerRadius }}
          >
            <img
              src={mediaUrl ? mediaUrl(item.image) : item.image}
              alt={item.title}
              loading="lazy"
              className={index % 2 === 0 ? "cr-kenburns" : "cr-kenburns-alt"}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(46,31,24,0.30) 75%, rgba(46,31,24,0.62) 100%)",
              }}
            />
          </div>
          <div
            className="absolute pointer-events-none transition-all duration-700"
            style={{
              top: 5, left: 5, right: 5, bottom: 5,
              borderRadius: innerRadius,
              border: "1px solid rgba(250, 244, 236, 0.35)",
            }}
          />
          <div className="absolute inset-x-0 bottom-4 md:bottom-5 flex flex-col items-center justify-end pointer-events-none text-center px-2">
            <p
              className="font-heading text-white text-2xl md:text-3xl font-light leading-none"
              style={{ textShadow: "0 2px 12px rgba(0,0,0,0.65)" }}
            >
              {item.price}
            </p>
          </div>
        </div>
        <p className="mt-4 md:mt-5 text-center font-heading text-lg md:text-xl font-light text-refuge-text tracking-tight transition-colors duration-500 group-hover:text-refuge-ruby">
          {item.title}
        </p>
        <p className="mt-1 text-center font-body text-[11px] tracking-[0.2em] uppercase text-refuge-soft/80">
          {item.unit}
        </p>
      </div>
    </button>
  );
}

export default function Experiences() {
  const { mediaUrl, buildWhatsAppUrl, content, saveText } = useContent();

  /**
   * Merge un EXP par défaut avec ses éventuelles surcharges CMS
   * (`content.experiences.<id>.<field>`). Retourne toujours la structure complète.
   */
  const cmsFor = (id) => content?.experiences?.[id] || {};
  const val = (id, field, fallback) => {
    const v = cmsFor(id)[field];
    return v !== undefined && v !== null && v !== "" ? v : fallback;
  };
  const valArr = (id, field, fallback) => {
    const v = cmsFor(id)[field];
    return Array.isArray(v) && v.length ? v : fallback;
  };
  /** état d'un encart (hidden / deleted) */
  const blk = (id, name) => cmsFor(id)?.blocks?.[name] || {};
  const isVisible = (id, name) => {
    const b = blk(id, name);
    return !b.hidden && !b.deleted;
  };
  const setBlockFlag = (id, name, flag, val = true) =>
    saveText(`experiences.${id}.blocks.${name}.${flag}`, val);
  const clearBlockFlags = (id, name) =>
    saveText(`experiences.${id}.blocks.${name}`, {});
  const heroEyebrow = val("_page", "eyebrow", "Les expériences");
  const heroTitle = val("_page", "title", "Sublimez");
  const heroTitleItalic = val("_page", "titleItalic", "votre séjour");
  const heroSub = val(
    "_page",
    "sub",
    "Trois rituels à ajouter à votre venue — un bain fleuri, un petit déjeuner servi au refuge, une mise en scène délicate."
  );

  return (
    <div data-testid="experiences-page" className="bg-refuge-bg min-h-screen">
      <InternalNav />

      {/* HEADER */}
      <section className="pt-32 md:pt-40 pb-14 md:pb-20 bg-refuge-bg">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <InlineText
            as="p"
            path="experiences._page.eyebrow"
            value={heroEyebrow}
            onSave={(v) => saveText("experiences._page.eyebrow", v)}
            className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby mb-4"
          />
          <h1 className="font-heading text-4xl md:text-6xl font-light text-refuge-text mb-6 leading-[1.05]">
            <InlineText
              path="experiences._page.title"
              value={heroTitle}
              onSave={(v) => saveText("experiences._page.title", v)}
            />{" "}
            <span className="italic">
              <InlineText
                path="experiences._page.titleItalic"
                value={heroTitleItalic}
                onSave={(v) => saveText("experiences._page.titleItalic", v)}
              />
            </span>
          </h1>
          <InlineText
            as="p"
            multiline
            path="experiences._page.sub"
            value={heroSub}
            onSave={(v) => saveText("experiences._page.sub", v)}
            className="font-heading italic font-light text-refuge-soft/85 text-lg md:text-xl leading-relaxed"
          />
        </div>
      </section>

      {/* Trois fenêtres immersives */}
      <section className="pb-16 md:pb-24 bg-refuge-bg">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-3 gap-4 sm:gap-8 md:gap-12">
            {EXPS.map((e, i) => (
              <ExperienceWindow key={e.id} item={e} index={i} mediaUrl={mediaUrl} />
            ))}
          </div>
        </div>
      </section>

      {/* Experiences detail */}
      {EXPS.map((x, idx) => {
        const Icon = x.Icon;
        return (
          <section
            key={x.id}
            id={x.id}
            data-testid={`experience-${x.id}`}
            className={`py-20 md:py-28 scroll-mt-24 ${idx % 2 === 0 ? "bg-white" : "bg-refuge-bg"}`}
          >
            <div className="max-w-6xl mx-auto px-6">
              <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center mb-14">
                <div>
                  <Link
                    to={`/experiences/${x.id}`}
                    data-testid={`exp-${x.id}-image-link`}
                    aria-label={`Ouvrir la page immersive ${x.title}`}
                    className="group block overflow-hidden rounded-2xl border border-refuge-border/60 relative"
                    style={{ boxShadow: "0 18px 40px -24px rgba(45,40,37,0.35)" }}
                  >
                    <img
                      src={mediaUrl(x.image)}
                      alt={x.title}
                      loading="lazy"
                      className="w-full aspect-[4/5] object-cover transition-transform duration-[1200ms] group-hover:scale-[1.03]"
                    />
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end justify-end p-5"
                      style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(0,0,0,0.45) 100%)" }}
                    >
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 text-refuge-ruby font-body text-[11px] tracking-[0.14em] uppercase">
                        Découvrir <ArrowUpRight size={11} />
                      </span>
                    </div>
                  </Link>
                </div>
                <div>
                  <InlineText
                    as="p"
                    path={`experiences.${x.id}.eyebrow`}
                    value={val(x.id, "eyebrow", x.eyebrow)}
                    onSave={(v) => saveText(`experiences.${x.id}.eyebrow`, v)}
                    className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby mb-4"
                  />
                  <h2 className="font-heading text-3xl md:text-5xl font-light text-refuge-text mb-4 leading-tight">
                    <InlineText
                      path={`experiences.${x.id}.title`}
                      value={val(x.id, "title", x.title)}
                      onSave={(v) => saveText(`experiences.${x.id}.title`, v)}
                    />
                  </h2>
                  <InlineText
                    as="p"
                    multiline
                    path={`experiences.${x.id}.sub`}
                    value={val(x.id, "sub", x.sub)}
                    onSave={(v) => saveText(`experiences.${x.id}.sub`, v)}
                    className="font-heading italic font-light text-refuge-soft/80 text-lg md:text-xl mb-4"
                  />
                  <InlineText
                    as="p"
                    multiline
                    path={`experiences.${x.id}.intro`}
                    value={val(x.id, "intro", x.intro)}
                    onSave={(v) => saveText(`experiences.${x.id}.intro`, v)}
                    className="font-body text-base text-refuge-soft leading-relaxed mb-6 max-w-md"
                  />
                  <div className="flex items-baseline gap-3 pb-6 mb-6 border-b border-refuge-border/60">
                    <span className="font-heading text-5xl md:text-6xl text-refuge-ruby font-light">
                      <InlineText
                        path={`experiences.${x.id}.price`}
                        value={val(x.id, "price", x.price)}
                        onSave={(v) => saveText(`experiences.${x.id}.price`, v)}
                      />
                    </span>
                    <InlineText
                      path={`experiences.${x.id}.unit`}
                      value={val(x.id, "unit", x.unit)}
                      onSave={(v) => saveText(`experiences.${x.id}.unit`, v)}
                      className="font-body text-sm text-refuge-soft tracking-wide"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to={`/experiences/${x.id}`}
                      data-testid={`exp-${x.id}-cta-immersive`}
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-refuge-ruby text-white font-body text-sm hover:bg-refuge-ruby/90 transition-all"
                    >
                      <InlineText
                        path={`experiences.${x.id}.ctaImmersiveLabel`}
                        value={val(x.id, "ctaImmersiveLabel", "Découvrir en immersion")}
                        onSave={(v) => saveText(`experiences.${x.id}.ctaImmersiveLabel`, v)}
                      />
                      <ArrowUpRight size={14} strokeWidth={1.3} />
                    </Link>
                    {x.id === "petit-dejeuner" && x.menu && (
                      <Link
                        to="/experiences/petit-dejeuner/menu"
                        data-testid={`exp-${x.id}-cta-menu`}
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-refuge-text text-refuge-text font-body text-sm hover:bg-refuge-text hover:text-white transition-all"
                      >
                        <InlineText
                          path={`experiences.${x.id}.ctaMenuLabel`}
                          value={val(x.id, "ctaMenuLabel", "Découvrir le menu")}
                          onSave={(v) => saveText(`experiences.${x.id}.ctaMenuLabel`, v)}
                        />
                        <ArrowUpRight size={14} strokeWidth={1.3} />
                      </Link>
                    )}
                  </div>
                </div>
              </div>

              {x.ambiance && isVisible(x.id, "ambiance") && (
                <BlockControls
                  blockId={`${x.id}-ambiance`}
                  hidden={blk(x.id, "ambiance").hidden}
                  onHide={() => setBlockFlag(x.id, "ambiance", "hidden", true)}
                  onShow={() => clearBlockFlags(x.id, "ambiance")}
                  onDelete={() => setBlockFlag(x.id, "ambiance", "deleted", true)}
                  className="mb-14 max-w-3xl mx-auto text-center"
                >
                  <span className="inline-block px-3 py-1 rounded-full font-body text-[10px] tracking-[0.2em] uppercase mb-4 bg-refuge-ruby/10 text-refuge-ruby">
                    <InlineText
                      path={`experiences.${x.id}.ambiance.status`}
                      value={val(x.id, "ambiance", null)?.status || x.ambiance.status}
                      onSave={(v) => saveText(`experiences.${x.id}.ambiance.status`, v)}
                    />
                  </span>
                  <h4 className="font-heading text-2xl md:text-3xl font-light text-refuge-text mb-2">
                    <InlineText
                      path={`experiences.${x.id}.ambiance.name`}
                      value={val(x.id, "ambiance", null)?.name || x.ambiance.name}
                      onSave={(v) => saveText(`experiences.${x.id}.ambiance.name`, v)}
                    />
                  </h4>
                  <InlineText
                    as="p"
                    path={`experiences.${x.id}.ambiance.tagline`}
                    value={val(x.id, "ambiance", null)?.tagline || x.ambiance.tagline}
                    onSave={(v) => saveText(`experiences.${x.id}.ambiance.tagline`, v)}
                    className="font-heading italic text-refuge-soft/80 mb-5"
                  />
                  <InlineList
                    items={valArr(x.id, "ambianceFeatures", x.ambiance.features)}
                    path={`experiences.${x.id}.ambianceFeatures`}
                    onSave={(a) => saveText(`experiences.${x.id}.ambianceFeatures`, a)}
                    wrapClassName="space-y-2 mb-5 inline-block text-left"
                    renderItem={(f) => (
                      <div className="flex items-start gap-3 font-body text-sm text-refuge-text">
                        <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-refuge-ruby shrink-0" />
                        {f}
                      </div>
                    )}
                  />
                  <InlineText
                    as="p"
                    multiline
                    path={`experiences.${x.id}.ambiance.note`}
                    value={val(x.id, "ambiance", null)?.note || x.ambiance.note}
                    onSave={(v) => saveText(`experiences.${x.id}.ambiance.note`, v)}
                    className="font-body text-xs italic text-refuge-soft/80"
                  />
                </BlockControls>
              )}

              <div className="grid md:grid-cols-2 gap-10 md:gap-16 pt-10 border-t border-refuge-border/60">
                {isVisible(x.id, "essential") && (
                  <BlockControls
                    blockId={`${x.id}-essential`}
                    hidden={blk(x.id, "essential").hidden}
                    onHide={() => setBlockFlag(x.id, "essential", "hidden", true)}
                    onShow={() => clearBlockFlags(x.id, "essential")}
                    onDelete={() => setBlockFlag(x.id, "essential", "deleted", true)}
                  >
                    <div className="flex items-center gap-3 mb-6">
                      <Icon size={18} className="text-refuge-ruby" strokeWidth={1.2} />
                      <InlineText
                        as="p"
                        path={`experiences.${x.id}.essentialEyebrow`}
                        value={val(x.id, "essentialEyebrow", "L'essentiel")}
                        onSave={(v) => saveText(`experiences.${x.id}.essentialEyebrow`, v)}
                        className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby"
                      />
                    </div>
                    <InlineText
                      as="p"
                      path={`experiences.${x.id}.essentialTitle`}
                      value={val(x.id, "essentialTitle", "Ce qui vous attend")}
                      onSave={(v) => saveText(`experiences.${x.id}.essentialTitle`, v)}
                      className="font-heading text-2xl font-light text-refuge-text mb-6"
                    />
                    <InlineList
                      items={valArr(x.id, "essential", x.essential)}
                      path={`experiences.${x.id}.essential`}
                      onSave={(a) => saveText(`experiences.${x.id}.essential`, a)}
                      wrapClassName="space-y-3"
                      renderItem={(t) => (
                        <div className="flex items-start gap-3 font-body text-sm text-refuge-text/90">
                          <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-refuge-rose shrink-0" />
                          {t}
                        </div>
                      )}
                    />
                  </BlockControls>
                )}
                {isVisible(x.id, "practical") && (
                  <BlockControls
                    blockId={`${x.id}-practical`}
                    hidden={blk(x.id, "practical").hidden}
                    onHide={() => setBlockFlag(x.id, "practical", "hidden", true)}
                    onShow={() => clearBlockFlags(x.id, "practical")}
                    onDelete={() => setBlockFlag(x.id, "practical", "deleted", true)}
                  >
                    <InlineText
                      as="p"
                      path={`experiences.${x.id}.practicalEyebrow`}
                      value={val(x.id, "practicalEyebrow", "Bon à savoir")}
                      onSave={(v) => saveText(`experiences.${x.id}.practicalEyebrow`, v)}
                      className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby mb-6"
                    />
                    <InlineText
                      as="p"
                      path={`experiences.${x.id}.practicalTitle`}
                      value={val(x.id, "practicalTitle", "Informations pratiques")}
                      onSave={(v) => saveText(`experiences.${x.id}.practicalTitle`, v)}
                      className="font-heading text-2xl font-light text-refuge-text mb-6"
                    />
                    <InlineList
                      items={valArr(x.id, "practical", x.practical)}
                      path={`experiences.${x.id}.practical`}
                      onSave={(a) => saveText(`experiences.${x.id}.practical`, a)}
                      wrapClassName="space-y-3"
                      renderItem={(t) => (
                        <div className="flex items-start gap-3 font-body text-sm text-refuge-text/90">
                          <span className="mt-[9px] w-1.5 h-1.5 rounded-full bg-refuge-rose shrink-0" />
                          {t}
                        </div>
                      )}
                    />
                    {x.vidangeCta && isVisible(x.id, "vidangeCta") && (
                      <BlockControls
                        blockId={`${x.id}-vidange`}
                        hidden={blk(x.id, "vidangeCta").hidden}
                        onHide={() => setBlockFlag(x.id, "vidangeCta", "hidden", true)}
                        onShow={() => clearBlockFlags(x.id, "vidangeCta")}
                        onDelete={() => setBlockFlag(x.id, "vidangeCta", "deleted", true)}
                        className="mt-6 inline-block"
                      >
                        <Link
                          to={x.vidangeCta.href}
                          data-testid={`exp-${x.id}-vidange-cta`}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-refuge-ruby/50 text-refuge-ruby font-body text-xs tracking-[0.14em] uppercase hover:bg-refuge-ruby hover:text-white transition-all"
                        >
                          <Droplets size={13} strokeWidth={1.4} />{" "}
                          <InlineText
                            path={`experiences.${x.id}.vidangeCtaLabel`}
                            value={val(x.id, "vidangeCtaLabel", x.vidangeCta.label)}
                            onSave={(v) => saveText(`experiences.${x.id}.vidangeCtaLabel`, v)}
                          />
                        </Link>
                      </BlockControls>
                    )}
                  </BlockControls>
                )}
              </div>

              {x.precautions && isVisible(x.id, "precautions") && (
                <BlockControls
                  blockId={`${x.id}-precautions`}
                  hidden={blk(x.id, "precautions").hidden}
                  onHide={() => setBlockFlag(x.id, "precautions", "hidden", true)}
                  onShow={() => clearBlockFlags(x.id, "precautions")}
                  onDelete={() => setBlockFlag(x.id, "precautions", "deleted", true)}
                  className="mt-12 max-w-3xl mx-auto rounded-2xl border border-refuge-border/60 px-6 py-6 md:px-8 md:py-7"
                >
                  <div
                    data-testid={`exp-${x.id}-precautions`}
                    style={{ background: "linear-gradient(180deg, rgba(232,184,189,0.10) 0%, rgba(184,153,104,0.06) 100%)", marginLeft: -24, marginRight: -24, marginTop: -24, marginBottom: -24, padding: "24px 24px 28px", borderRadius: 16 }}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <span className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center mt-0.5"
                        style={{ background: "rgba(232,184,189,0.30)", border: "1px solid rgba(139,37,48,0.22)" }}>
                        <Info size={15} strokeWidth={1.5} className="text-refuge-ruby" />
                      </span>
                      <div className="flex-1">
                        <h4 className="font-heading text-xl font-light text-refuge-text leading-tight">
                          <InlineText
                            path={`experiences.${x.id}.precautions.title`}
                            value={val(x.id, "precautions", null)?.title || x.precautions.title}
                            onSave={(v) => saveText(`experiences.${x.id}.precautions.title`, v)}
                          />
                        </h4>
                      </div>
                    </div>
                    {(val(x.id, "precautions", null)?.intro || x.precautions.intro) && (
                      <InlineText
                        as="p"
                        multiline
                        path={`experiences.${x.id}.precautions.intro`}
                        value={val(x.id, "precautions", null)?.intro || x.precautions.intro}
                        onSave={(v) => saveText(`experiences.${x.id}.precautions.intro`, v)}
                        className="font-body text-sm text-refuge-text/90 leading-relaxed mb-5"
                      />
                    )}
                    {(valArr(x.id, "precautions.avoid", x.precautions.avoid || []).length > 0) && (
                      <>
                        <InlineText
                          as="p"
                          path={`experiences.${x.id}.precautions.avoidLabel`}
                          value={val(x.id, "precautions", null)?.avoidLabel || x.precautions.avoidLabel}
                          onSave={(v) => saveText(`experiences.${x.id}.precautions.avoidLabel`, v)}
                          className="font-body text-[10px] tracking-[0.24em] uppercase text-refuge-soft mb-2"
                        />
                        <p className="font-body text-sm text-refuge-text/85 mb-2">
                          Par précaution, nous vous invitons à éviter cette expérience en cas :
                        </p>
                        <InlineList
                          items={cmsFor(x.id)?.precautions?.avoid || x.precautions.avoid || []}
                          path={`experiences.${x.id}.precautions.avoid`}
                          onSave={(a) => saveText(`experiences.${x.id}.precautions.avoid`, a)}
                          wrapClassName="space-y-2 mb-5"
                          renderItem={(it) => (
                            <div className="flex items-start gap-3 font-body text-sm text-refuge-text/85">
                              <span className="mt-[8px] w-1.5 h-1.5 rounded-full bg-refuge-ruby/60 shrink-0" />
                              {it}
                            </div>
                          )}
                        />
                      </>
                    )}
                    {(valArr(x.id, "precautions.tips", x.precautions.tips || []).length > 0) && (
                      <>
                        <InlineText
                          as="p"
                          path={`experiences.${x.id}.precautions.tipsLabel`}
                          value={val(x.id, "precautions", null)?.tipsLabel || x.precautions.tipsLabel}
                          onSave={(v) => saveText(`experiences.${x.id}.precautions.tipsLabel`, v)}
                          className="font-body text-[10px] tracking-[0.24em] uppercase text-refuge-soft mb-2"
                        />
                        <InlineList
                          items={cmsFor(x.id)?.precautions?.tips || x.precautions.tips || []}
                          path={`experiences.${x.id}.precautions.tips`}
                          onSave={(a) => saveText(`experiences.${x.id}.precautions.tips`, a)}
                          wrapClassName="space-y-2"
                          renderItem={(it) => (
                            <div className="flex items-start gap-3 font-body text-sm text-refuge-text/85">
                              <Check size={13} strokeWidth={1.6} className="mt-[6px] shrink-0" style={{ color: "#C9A961" }} />
                              {it}
                            </div>
                          )}
                        />
                      </>
                    )}
                    {(val(x.id, "precautions", null)?.footer || x.precautions.footer) && (
                      <InlineText
                        as="p"
                        multiline
                        path={`experiences.${x.id}.precautions.footer`}
                        value={val(x.id, "precautions", null)?.footer || x.precautions.footer}
                        onSave={(v) => saveText(`experiences.${x.id}.precautions.footer`, v)}
                        className="font-body italic text-sm text-refuge-soft/85 mt-5 leading-relaxed"
                      />
                    )}
                  </div>
                </BlockControls>
              )}
            </div>
          </section>
        );
      })}

      {/* Final CTA */}
      <section className="bg-refuge-ruby py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <InlineText
            as="p"
            path="experiences._final.eyebrow"
            value={val("_final", "eyebrow", "Réservation")}
            onSave={(v) => saveText("experiences._final.eyebrow", v)}
            className="font-body text-xs tracking-[0.2em] uppercase text-refuge-rose mb-6"
          />
          <h2 className="font-heading text-3xl md:text-5xl font-light text-white mb-10 leading-tight">
            <InlineText
              path="experiences._final.title"
              value={val("_final", "title", "Comment souhaitez-vous")}
              onSave={(v) => saveText("experiences._final.title", v)}
            />{" "}
            <span className="italic text-refuge-rose">
              <InlineText
                path="experiences._final.titleItalic"
                value={val("_final", "titleItalic", "vivre l'expérience")}
                onSave={(v) => saveText("experiences._final.titleItalic", v)}
              />
            </span>{" "}?
          </h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/reserver"
              data-testid="exp-final-reserver"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-refuge-ruby font-body text-sm hover:bg-white/95 transition-all"
            >
              <InlineText
                path="experiences._final.ctaReserverLabel"
                value={val("_final", "ctaReserverLabel", "Choisir mes dates")}
                onSave={(v) => saveText("experiences._final.ctaReserverLabel", v)}
              />
              <ArrowUpRight size={16} />
            </Link>
            <a
              href={buildWhatsAppUrl(WA_TEMPLATE("une expérience"))}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="exp-final-whatsapp"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white text-white font-body text-sm hover:bg-white hover:text-refuge-ruby transition-all"
            >
              <MessageCircle size={16} />{" "}
              <InlineText
                path="experiences._final.ctaWhatsappLabel"
                value={val("_final", "ctaWhatsappLabel", "J'ai déjà réservé")}
                onSave={(v) => saveText("experiences._final.ctaWhatsappLabel", v)}
              />
            </a>
          </div>
          <InlineText
            as="p"
            multiline
            path="experiences._final.footer"
            value={val("_final", "footer", "Après avoir choisi vos dates, vous pourrez sélectionner les expériences souhaitées lors de la réservation.")}
            onSave={(v) => saveText("experiences._final.footer", v)}
            className="font-body text-sm text-white/70 mt-8 max-w-md mx-auto leading-relaxed"
          />
        </div>
      </section>

      <div className="bg-refuge-bg py-10 text-center">
        <Link
          to="/accueil"
          data-testid="back-to-hub"
          className="inline-flex items-center gap-2 font-body text-xs tracking-[0.18em] uppercase text-refuge-soft hover:text-refuge-ruby transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.3} /> Retour à l'accueil
        </Link>
      </div>
    </div>
  );
}
